import { blockStatuses } from './constants';

const getBlockStatus = (allCount, blockedCount, unblockedCount) => {
  switch (true) {
    case blockedCount === allCount:
      return blockStatuses.blocked;

    case unblockedCount === allCount:
      return blockStatuses.unblocked;

    default:
      return blockStatuses.indeterminate;
  }
};

class Desk {
  constructor(desk) {
    this._payload = desk;
    this._cachedDaysPayload = new Map();
    this._cachedDaysSummary = new Map();
  }

  get id() {
    return this._payload.deskId;
  }

  get payload() {
    return this._payload;
  }

  getDayPayload(isoDateStr) {
    const cached = this._cachedDaysPayload.get(isoDateStr);

    if (cached !== undefined) return cached;

    const result =
      this._payload.dates.find((d) => d.date === isoDateStr) ?? null;

    this._cachedDaysPayload.set(isoDateStr, result);

    return result;
  }

  getIntervals(isoDateStr) {
    return this.getDayPayload(isoDateStr)?.intervals ?? [];
  }

  getDaySummary(isoDateStr) {
    const cached = this._cachedDaysSummary.get(isoDateStr);

    if (cached !== undefined) return cached;

    const payload = this.getDayPayload(isoDateStr);

    if (payload === null) {
      this._cachedDaysSummary.set(isoDateStr, null);
      return null;
    }

    const all = new Set();
    const blocked = [];
    const unblocked = [];
    let busyCount = 0;

    if (payload.intervals?.length) {
      payload.intervals.forEach((interval) => {
        const { id, intervalId, blocked: isBlocked, busy } = interval;

        all.add(intervalId);

        const blockContainer = isBlocked ? blocked : unblocked;
        blockContainer.push(id);

        if (busy) busyCount++;
      });
    }

    const result = {
      blockStatus: getBlockStatus(all.size, blocked.length, unblocked.length),
      allIntervals: all,
      blockedIntervals: blocked,
      unblockedIntervals: unblocked,
      deletable: busyCount < all.size,
    };

    this._cachedDaysSummary.set(isoDateStr, result);

    return result;
  }
}

export class SelectedPeriodInfo {
  constructor(desks) {
    this._desks = [];
    this._deskIdToIndex = new Map();
    this._cachedDaysSummary = new Map();

    desks.forEach((desk, index) => {
      const deskModel = new Desk(desk);
      this._desks.push(deskModel);
      this._deskIdToIndex.set(deskModel.id, index);
    });
  }

  getDaySummary(isoDateStr) {
    const cached = this._cachedDaysSummary.get(isoDateStr);

    if (cached !== undefined) return cached;

    const all = new Set();
    const blocked = [];
    const unblocked = [];
    let intervalsCount = 0;
    let busyCount = 0;

    this._desks.forEach((deskModel) => {
      const intervals = deskModel.getDayPayload(isoDateStr)?.intervals;

      if (!intervals?.length) return;

      intervalsCount += intervals.length;

      intervals.forEach((interval) => {
        const { id, intervalId, blocked: isBlocked, busy } = interval;

        all.add(intervalId);

        const blockContainer = isBlocked ? blocked : unblocked;
        blockContainer.push(id);

        if (busy) busyCount++;
      });
    });

    if (all.size === 0) {
      this._cachedDaysSummary.set(isoDateStr, null);
      return null;
    }

    const result = {
      blockStatus: getBlockStatus(
        intervalsCount,
        blocked.length,
        unblocked.length
      ),
      allIntervals: all,
      blockedIntervals: blocked,
      unblockedIntervals: unblocked,
      deletable: busyCount < intervalsCount,
    };

    this._cachedDaysSummary.set(isoDateStr, result);

    return result;
  }

  getDeskDaySummary(deskId, isoDateStr) {
    return this._getDeskModel(deskId)?.getDaySummary(isoDateStr) ?? null;
  }

  getDeskDayIntervals(deskId, isoDateStr) {
    return this._getDeskModel(deskId)?.getIntervals(isoDateStr) ?? [];
  }

  getDateId(isoDateStr) {
    let result;

    for (const deskModel of this._desks) {
      const dayPayload = deskModel.getDayPayload(isoDateStr);

      if (dayPayload) {
        result = dayPayload.dateId;
        break;
      }
    }

    return result;
  }

  _getDeskModel(deskId) {
    const index = this._deskIdToIndex.get(deskId);
    return index === undefined ? index : this._desks[index];
  }
}
